import React, { useEffect, useRef } from 'react';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import { Tooltip } from '@mui/material';
import * as S from '../../styles';

const VALIDATION_TOOLTIP_PROPS = {
  tooltip: {
    sx: {
      fontSize: '0.7rem',
      fontWeight: 500,
      backgroundColor: '#d32f2f',
    },
  },
  arrow: {
    sx: {
      color: '#d32f2f',
    },
  },
} as const;

const VALIDATION_POPPER_PROPS = {
  modifiers: [
    {
      name: 'offset' as const,
      options: {
        offset: [0, -5],
      },
    },
  ],
};

interface EditableCellProps {
  value: any;
  isEditing: boolean;
  onDoubleClick?: () => void;
  onSave: (value: any) => void;
  onCancel: () => void;
  children: React.ReactNode;
  editComponent: React.ReactNode;
  maintainExpandedWidth?: boolean;
  hasValidationError?: boolean;
  validationMessage?: string;
  validationStatus?: 'active' | 'fading' | null;
}

export const EditableCell: React.FC<EditableCellProps> = ({
  value: _value,
  isEditing,
  onDoubleClick,
  onSave: _onSave,
  onCancel,
  children,
  editComponent,
  maintainExpandedWidth = false,
  hasValidationError = false,
  validationMessage,
  validationStatus = null,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (isEditing) {
        if (event.key === 'Escape') {
          onCancel();
        }
      }
    };

    if (isEditing) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isEditing, onCancel]);

  if (isEditing) {
    return (
      <S.EditingContainer
        ref={containerRef}
        $hasValidationError={hasValidationError}
        $validationStatus={validationStatus}
      >
        {editComponent}
        {hasValidationError && validationMessage && (
          <Tooltip
            title={validationMessage}
            arrow
            placement='top'
            componentsProps={VALIDATION_TOOLTIP_PROPS}
            PopperProps={VALIDATION_POPPER_PROPS}
          >
            <S.ValidationErrorIcon $status={validationStatus}>
              <PriorityHighIcon />
            </S.ValidationErrorIcon>
          </Tooltip>
        )}
      </S.EditingContainer>
    );
  }

  if (maintainExpandedWidth) {
    return (
      <S.ExpandedDisplayContainer
        ref={containerRef}
        onDoubleClick={onDoubleClick}
      >
        {children}
      </S.ExpandedDisplayContainer>
    );
  }

  return (
    <S.DisplayContainer ref={containerRef} onDoubleClick={onDoubleClick}>
      {children}
    </S.DisplayContainer>
  );
};
